/* eslint-disable react/jsx-no-bind */
import {
  Box,
  Button,
  Divider,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  Textarea,
  VStack,
} from "@chakra-ui/react";
import { FC, useLayoutEffect, useState } from "react";

import {
  Es5LintError,
  extractEs5LintError,
  formatEs5LintError,
} from "./es5LintError";
import {
  MODULE_MODAL_CONTENT_Z_INDEX,
  MODULE_MODAL_OVERLAY_Z_INDEX,
} from "./moduleModalStacking";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (
    name: string,
    content: string,
    description: string | undefined
  ) => Promise<void>;
};

export const ModuleCreateModal: FC<Props> = ({ isOpen, onClose, onSubmit }) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [content, setContent] = useState("");
  const [lintError, setLintError] = useState<Es5LintError | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // `return null` when closed does not unmount this component, so clear fields
  // when the dialog opens. `useLayoutEffect` avoids one frame of stale values.
  useLayoutEffect(() => {
    if (!isOpen) return;
    setName("");
    setDescription("");
    setContent("");
    setLintError(null);
    setIsSubmitting(false);
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const handleSubmit = async () => {
    if (!name.trim()) return;

    setIsSubmitting(true);
    setLintError(null);
    try {
      await onSubmit(name.trim(), content, description.trim() || undefined);
      onClose();
    } catch (error) {
      const es5Error = extractEs5LintError(error);
      if (es5Error) {
        setLintError(es5Error);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    onClose();
  };

  const isValid = name.trim().length > 0;

  return (
    <Modal
      isOpen
      onClose={handleClose}
      size="full"
      closeOnOverlayClick
      scrollBehavior="inside"
      blockScrollOnMount={false}
    >
      <ModalOverlay zIndex={MODULE_MODAL_OVERLAY_Z_INDEX} />
      <ModalContent
        containerProps={{
          zIndex: MODULE_MODAL_CONTENT_Z_INDEX,
        }}
        width="90vw"
        height="90vh"
        maxWidth="90vw"
        maxHeight="90vh"
        margin="auto"
        display="flex"
        flexDirection="column"
        overflow="hidden"
      >
        <ModalHeader flexShrink={0}>
          <Text fontWeight="semibold">新規モジュール作成</Text>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody
          display="flex"
          flexDirection="column"
          gap={0}
          pb={0}
          flex={1}
          minH={0}
          overflow="hidden"
        >
          <VStack align="stretch" spacing={4} flexShrink={0} pb={2}>
            <Text fontSize="sm" fontWeight="semibold" color="gray.700">
              モジュール情報
            </Text>
            <FormControl isRequired>
              <FormLabel fontSize="sm">モジュール名</FormLabel>
              <Input
                size="sm"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="例: ヘッダートラッキング"
              />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm">説明</FormLabel>
              <Textarea
                size="sm"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="このモジュールの用途や設定方法を入力してください"
                rows={2}
                resize="vertical"
              />
            </FormControl>
            <Divider />
            <Text fontSize="sm" fontWeight="semibold" color="gray.700">
              初期コード (対応: ES5)
            </Text>
          </VStack>

          <Box flex={1} minHeight={0}>
            <Textarea
              value={content}
              onChange={(e) => {
                setContent(e.target.value);
                if (lintError) setLintError(null);
              }}
              placeholder="JavaScript コードを入力..."
              fontFamily="Monaco, Consolas, 'Courier New', monospace"
              fontSize="14px"
              resize="none"
              height="100%"
              border="1px solid"
              borderColor={lintError ? "red.400" : "gray.200"}
              _focus={{
                borderColor: lintError ? "red.500" : "blue.500",
                boxShadow: lintError
                  ? "0 0 0 1px #E53E3E"
                  : "0 0 0 1px #3182ce",
              }}
            />
          </Box>

          {lintError && (
            <Text flexShrink={0} color="red.500" fontSize="sm" pt={2}>
              {formatEs5LintError(lintError)}
            </Text>
          )}

          <HStack spacing={3} justifyContent="flex-end" flexShrink={0} py={4}>
            <Button variant="ghost" onClick={handleClose}>
              キャンセル
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleSubmit}
              isDisabled={!isValid}
              isLoading={isSubmitting}
            >
              作成
            </Button>
          </HStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
